<template>
  <v-row
    no-gutters
    justify="start"
    class="message-row"
  >
    <v-col
      cols="12"
      sm="12"
      md="12"
      class="message-col"
    >
      <div class="message-wrap">
        <div class="message-text">
          <template v-for="(segment, index) in contentSegments">
            <div
              v-if="segment.type === 'markdown'"
              :key="'md-' + index"
              class="markdown-body"
              v-html="segment.html"
            />

            <v-row
              v-else-if="isLastMessage"
              :key="'wg-' + index"
              class="widget-row"
            >
              <ai-widget
                v-for="(widget, widgetIndex) in segment.widgets"
                :key="widgetIndex"
                :widget="widget"
                @select="handleSuggestionSelect"
              />
            </v-row>
          </template>
        </div>

        <div
          v-if="isMessageStopped"
          class="stopped-response"
        >
          <div class="stopped-line" />
          <span class="stopped-text">You stopped this response</span>
          <div class="stopped-line" />
        </div>

        <div class="message-actions">
          <!-- Rerun Button -->
          <button
            ref="rerunButton"
            type="button"
            class="action-button rerun-button"
            aria-label="Rerun prompt"
            :disabled="streamingActive || editingActive"
            @click="rerunMessage"
            @mouseenter="showTooltip('rerun')"
            @mouseleave="hideTooltip('rerun')"
          >
            <v-icon size="20">
              mdi-refresh
            </v-icon>
          </button>

          <!-- Copy Button -->
          <button
            ref="copyButton"
            type="button"
            class="action-button copy-button"
            :aria-label="copied ? 'Copied message' : 'Copy message'"
            @click="copyMessage"
            @mouseenter="showTooltip('copy')"
            @mouseleave="hideTooltip('copy')"
          >
            <v-icon size="16">
              {{ copied ? 'mdi-check' : 'mdi-content-copy' }}
            </v-icon>
          </button>
        </div>
      </div>
    </v-col>
  </v-row>
</template>

<script>
import MarkdownIt from 'markdown-it'
import markdownItDeflist from 'markdown-it-deflist'
import markdownItFootnote from 'markdown-it-footnote'
import markdownItTexmath from 'markdown-it-texmath'
import katex from 'katex'
import 'katex/dist/katex.min.css'
import DOMPurify from 'dompurify'
import AiChatMessageStatus from '@/ai/model/AiChatMessageStatus'
import AiWidget from './AiWidget.vue'
import aiWidgetPlugin, { WIDGET_MARKER } from '@/ai/markdown/AiWidgetPlugin'

const md = new MarkdownIt({
  html   : true,
  breaks : true,
  linkify: true
})

md.use(markdownItDeflist)
md.use(markdownItFootnote)
md.use(markdownItTexmath, {
  engine      : katex,
  delimiters  : ['dollars', 'brackets', 'gitlab', 'beg_end'],
  katexOptions: {
    throwOnError: false,
    displayMode : true
  }
})
md.use(aiWidgetPlugin)

const WIDGET_TAG_REGEX = /<ai-widget type="([a-z0-9-]+)">([\s\S]*?)<\/ai-widget>/gi
const WIDGET_OPEN_PREFIX = '<ai-widget'
const WIDGET_CLOSE_TAG = '</ai-widget>'

export default {
  name: 'AssistantMessage',

  components: {
    AiWidget
  },

  dataStore: {
    assistantStore: 'AssistantStore'
  },

  props: {
    message: {
      type    : Object,
      required: true
    },

    animate: {
      type   : Boolean,
      default: false
    },

    streamingActive: {
      type   : Boolean,
      default: false
    },

    isLastMessage: {
      type   : Boolean,
      default: false
    }
  },

  data () {
    return {
      copied              : false,
      copiedTimer         : null,
      displayedContent    : '',
      streamingFrame      : null,
      streamingInterrupted: false,
      tooltipElement      : null,
      resizeFrame         : null
    }
  },

  computed: {
    messageContent () {
      return String(this.message?.content || '')
    },

    contentSegments () {
      const rawContent = (this.animate || this.streamingInterrupted)
        ? this.displayedContent
        : this.messageContent

      const safeContent = this.trimIncompleteTag(rawContent)
      const content = this.normalizeAlertBlocks(safeContent)

      return this.parseContentSegments(content)
    },

    copyContent () {
      return this.messageContent
        .replace(WIDGET_TAG_REGEX, '')
        .trim()
    },

    editingActive () {
      return this.assistantStore.Editing.Active
    },

    isMessageStopped () {
      return this.message?.status === AiChatMessageStatus.STOPPED
    }
  },

  watch: {
    displayedContent (newContent) {
      this.assistantStore.Streaming.DisplayedContent = newContent
    },

    messageContent: {
      immediate: true,
      handler (newContent) {
        if (!this.animate) {
          if (!this.streamingInterrupted) {
            this.displayedContent = newContent
          }
          return
        }

        if (!newContent.startsWith(this.displayedContent)) {
          this.displayedContent = ''
        }

        this.startStreaming()
      }
    },

    animate: {
      immediate: true,
      handler (value) {
        if (value) {
          this.streamingInterrupted = false

          if (!this.messageContent.startsWith(this.displayedContent)) {
            this.displayedContent = ''
          }

          this.startStreaming()
          return
        }

        this.stopStreaming({ interrupted: true })
      }
    }
  },

  mounted () {
    this.$nextTick(this.enhanceMarkdown)
    window.addEventListener('resize', this.handleWindowResize)
  },

  updated () {
    this.$emit('content-rendered')
    this.$nextTick(this.enhanceMarkdown)
  },

  beforeDestroy () {
    this.clearCopiedTimer()
    this.stopStreaming()
    this.removeTooltip()
    window.removeEventListener('resize', this.handleWindowResize)
    if (this.resizeFrame) {
      cancelAnimationFrame(this.resizeFrame)
      this.resizeFrame = null
    }
  },

  methods: {
    enhanceMarkdown () {
      this.enhanceAlertBlockquotes()
      this.enhanceCodeBlocks()
      this.enhanceTables()
    },

    renderMarkdown (text) {
      const rendered = md.render(text)

      return DOMPurify.sanitize(rendered, {
        ADD_TAGS: [
          'details', 'summary',
          'math', 'mrow', 'annotation', 'semantics', 'mtext', 'mn', 'mo', 'mi', 'mspace', 'msup', 'msub', 'msubsup', 'mfrac', 'mover', 'munder', 'munderover', 'mtable', 'mtr', 'mtd', 'msqrt', 'mroot',
          'svg', 'path', 'line', 'rect'
        ],
        ADD_ATTR: [
          'open', 'class', 'style', 'id', 'aria-hidden', 'viewBox', 'd', 'stroke', 'stroke-width', 'fill', 'xmlns'
        ]
      })
    },

    parseContentSegments (content) {
      const rawSegments = this.buildRawSegments(content)

      return this.mergeAdjacentWidgetGroups(rawSegments)
    },

    buildRawSegments (content) {
      const rendered = this.renderMarkdown(content)
      const parts = rendered.split(WIDGET_MARKER)
      const segments = []

      for (let i = 0; i < parts.length; i += 3) {
        const html = parts[i]

        if (html) {
          segments.push({ type: 'markdown', html, raw: html })
        }

        const widgetType = parts[i + 1]
        const encodedPayload = parts[i + 2]

        if (widgetType !== undefined) {
          const widget = this.parseWidgetPayload(widgetType, encodedPayload)
          segments.push({ type: 'widget-group', widgets: [widget] })
        }
      }

      return segments.length ? segments : [{ type: 'markdown', html: rendered, raw: rendered }]
    },

    mergeAdjacentWidgetGroups (segments) {
      const merged = []

      segments.forEach((segment) => {
        const previous = merged[merged.length - 1]
        const isBlankMarkdown =
          segment.type === 'markdown' && !segment.raw.trim()

        // Drop blank markdown segments that only exist because of
        // whitespace/newlines between widget tags, so grouping below
        // can see the widget-groups as adjacent.
        if (isBlankMarkdown && merged.length && this.nextIsWidgetGroup(segments, segments.indexOf(segment))) {
          return
        }

        if (
          segment.type === 'widget-group' &&
          previous?.type === 'widget-group' &&
          previous.widgets[0].type === segment.widgets[0].type &&
          segment.widgets[0].type === 'suggestion'
        ) {
          previous.widgets.push(...segment.widgets)
          return
        }

        merged.push(segment)
      })

      return merged
    },

    nextIsWidgetGroup (segments, index) {
      for (let i = index + 1; i < segments.length; i += 1) {
        if (segments[i].type === 'widget-group') {
          return true
        }
        if (segments[i].type === 'markdown' && segments[i].raw.trim()) {
          return false
        }
      }
      return false
    },

    parseWidgetPayload (type, encodedPayload) {
      const trimmed = decodeURIComponent(encodedPayload || '').trim()

      try {
        return { type, data: JSON.parse(trimmed) }
      } catch (error) {
        return { type, data: { content: trimmed, displayContent: trimmed } }
      }
    },

    trimIncompleteTag (content) {
      const text = String(content || '')

      // Hide an opening tag until its closing tag has fully streamed in.
      const lastOpenIndex = text.lastIndexOf(WIDGET_OPEN_PREFIX)
      if (lastOpenIndex !== -1) {
        const closeIndex = text.indexOf(WIDGET_CLOSE_TAG, lastOpenIndex)
        if (closeIndex === -1) {
          return text.slice(0, lastOpenIndex)
        }
      }

      // Hide a partially-typed opening tag, e.g. "<ai-widg".
      for (let length = WIDGET_OPEN_PREFIX.length - 1; length >= 2; length -= 1) {
        if (text.endsWith(WIDGET_OPEN_PREFIX.slice(0, length))) {
          return text.slice(0, text.length - length)
        }
      }

      return text
    },

    handleSuggestionSelect (suggestion) {
      if (this.streamingActive || this.editingActive) {
        return
      }
      this.$emit('suggestion-select', suggestion)
    },

    normalizeAlertBlocks (content) {
      const lines = String(content || '').split('\n')
      const output = []
      let index = 0

      while (index < lines.length) {
        const alertMatch = lines[index].match(/^\s*\[!(NOTE|TIP|WARNING|CAUTION)\]\s*$/i)

        if (!alertMatch) {
          output.push(lines[index])
          index += 1
          continue
        }

        output.push(`> [!${alertMatch[1].toUpperCase()}]`)
        index += 1

        while (index < lines.length && lines[index].trim() !== '') {
          output.push(`> ${lines[index]}`)
          index += 1
        }
      }

      return output.join('\n')
    },

    enhanceAlertBlockquotes () {
      const alertLabels = {
        note   : 'Note',
        tip    : 'Tip',
        warning: 'Warning',
        caution: 'Caution'
      }

      const blockquotes = this.$el.querySelectorAll('.markdown-body blockquote')

      blockquotes.forEach((block) => {
        if (block.classList.contains('md-alert')) {
          return
        }

        const firstParagraph = block.querySelector('p')
        if (!firstParagraph) {
          return
        }

        const markerMatch = firstParagraph.textContent.trim().match(
          /^\[!(NOTE|TIP|WARNING|CAUTION)\]\s*/i
        )

        if (!markerMatch) {
          return
        }

        const type = markerMatch[1].toLowerCase()
        block.classList.add('md-alert', `md-alert-${type}`)

        firstParagraph.textContent = firstParagraph.textContent.replace(
          /^\s*\[!(NOTE|TIP|WARNING|CAUTION)\]\s*/i,
          ''
        )

        if (!firstParagraph.textContent.trim()) {
          firstParagraph.remove()
        }

        const title = document.createElement('div')
        title.className = 'md-alert-title'
        title.textContent = alertLabels[type]
        block.insertBefore(title, block.firstChild)
      })
    },

    showTooltip (type) {
      const button = type === 'rerun'
        ? this.$refs.rerunButton
        : this.$refs.copyButton

      const text = type === 'rerun'
        ? 'Rerun prompt'
        : (this.copied ? 'Copied!' : 'Copy response')

      if (!button) return

      this.removeTooltip()

      const tooltip = document.createElement('div')
      tooltip.className = 'assistant-tooltip'
      tooltip.textContent = text

      const rect = button.getBoundingClientRect()
      tooltip.style.cssText = `
        position: fixed;
        top: ${rect.bottom + 4}px;
        left: ${rect.left + rect.width / 2}px;
        transform: translateX(-50%);
        padding: 3px 8px;
        background: white;
        color: #041f30;
        font-family: 'Inter';
        font-size: 11px;
        font-weight: 400;
        line-height: 21px;
        border-radius: 4px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
        white-space: nowrap;
        z-index: 99999;
        pointer-events: none;
        opacity: 0;
        animation: tooltipFadeIn 0.15s ease forwards;
      `

      document.body.appendChild(tooltip)
      this.tooltipElement = tooltip
    },

    hideTooltip () {
      this.removeTooltip()
    },

    removeTooltip () {
      if (this.tooltipElement) {
        this.tooltipElement.remove()
        this.tooltipElement = null
      }
    },

    startStreaming () {
      if (this.streamingFrame) {
        return
      }
      this.assistantStore.Streaming.AnimationActive = true
      const step = () => {
        this.streamingFrame = null

        if (!this.animate) {
          return
        }

        if (this.displayedContent === this.messageContent) {
          if (!this.assistantStore.Streaming.Active) {
            this.assistantStore.Streaming.AnimationActive = false
            this.assistantStore.Messages.SendEnabled = true
            return
          }
          this.streamingFrame = requestAnimationFrame(step)
          return
        }

        const remaining = this.messageContent.length - this.displayedContent.length
        const charactersPerFrame = Math.max(
          3,
          Math.min(12, Math.ceil(remaining / 35))
        )

        const nextLength = Math.min(
          this.displayedContent.length + charactersPerFrame,
          this.messageContent.length
        )

        this.displayedContent = this.messageContent.slice(0, nextLength)
        this.streamingFrame = requestAnimationFrame(step)
      }

      this.streamingFrame = requestAnimationFrame(step)
    },

    stopStreaming ({ interrupted = false } = {}) {
      if (interrupted) {
        this.streamingInterrupted = true
      }
      this.assistantStore.Streaming.AnimationActive = false
      if (!this.streamingFrame) {
        return
      }
      cancelAnimationFrame(this.streamingFrame)
      this.streamingFrame = null
    },

    async copyMessage () {
      if (!this.copyContent) {
        return
      }

      await this.copyText(this.copyContent)
      this.markCopied()
    },

    async copyText (value) {
      if (
        navigator.clipboard &&
        typeof navigator.clipboard.writeText === 'function'
      ) {
        await navigator.clipboard.writeText(value)
        return
      }

      this.copyWithExecCommand(value)
    },

    copyWithExecCommand (value) {
      const textarea = document.createElement('textarea')
      textarea.value = value
      textarea.setAttribute('readonly', '')
      textarea.style.position = 'fixed'
      textarea.style.opacity = '0'

      document.body.appendChild(textarea)
      textarea.select()
      document.execCommand('copy')
      document.body.removeChild(textarea)
    },

    rerunMessage () {
      if (this.streamingActive || this.editingActive) {
        return
      }
      this.$emit('rerun-prompt', this.message)
    },

    tableToCSV (table) {
      const rows = table.querySelectorAll('tr')
      const csvRows = []

      rows.forEach((row) => {
        const cells = row.querySelectorAll('th, td')
        const csvCells = []

        cells.forEach((cell) => {
          let cellText = cell.textContent.trim()

          if (cellText.includes(',') || cellText.includes('"') || cellText.includes('\n')) {
            cellText = '"' + cellText.replace(/"/g, '""') + '"'
          }

          csvCells.push(cellText)
        })

        csvRows.push(csvCells.join(','))
      })

      return csvRows.join('\n')
    },

    downloadCSV (csvContent, filename = 'table.csv') {
      const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')

      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob)
        link.setAttribute('href', url)
        link.setAttribute('download', filename)
        link.style.visibility = 'hidden'

        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        URL.revokeObjectURL(url)
      }
    },

    createCustomScrollbar (scrollElement, container) {
      if (container.querySelector('.custom-scrollbar')) {
        const scrollbar = container.querySelector('.custom-scrollbar')
        this.updateCustomScrollbar(scrollElement, scrollbar)
        return
      }

      const scrollbar = document.createElement('div')
      scrollbar.className = 'custom-scrollbar'

      const track = document.createElement('div')
      track.className = 'custom-scrollbar-track'

      const thumb = document.createElement('div')
      thumb.className = 'custom-scrollbar-thumb'

      track.appendChild(thumb)
      scrollbar.appendChild(track)
      container.appendChild(scrollbar)

      const handleScroll = () => {
        this.updateCustomScrollbar(scrollElement, scrollbar)
      }

      scrollElement.addEventListener('scroll', handleScroll, { passive: true })

      let dragging = false
      let dragStartX = 0
      let dragStartScrollLeft = 0

      thumb.addEventListener('mousedown', (event) => {
        event.preventDefault()

        dragging = true
        dragStartX = event.clientX
        dragStartScrollLeft = scrollElement.scrollLeft

        document.body.classList.add('custom-scrollbar-dragging')
      })

      const handleMouseMove = (event) => {
        if (!dragging) {
          return
        }

        const trackWidth = track.clientWidth
        const thumbWidth = thumb.offsetWidth
        const availableThumbTravel = trackWidth - thumbWidth

        if (availableThumbTravel <= 0) {
          return
        }

        const maxScrollLeft =
          scrollElement.scrollWidth - scrollElement.clientWidth

        const deltaX = event.clientX - dragStartX

        scrollElement.scrollLeft =
          dragStartScrollLeft +
          (deltaX / availableThumbTravel) * maxScrollLeft
      }

      const handleMouseUp = () => {
        if (!dragging) {
          return
        }

        dragging = false
        document.body.classList.remove('custom-scrollbar-dragging')
      }

      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)

      track.addEventListener('mousedown', (event) => {
        if (event.target === thumb) {
          return
        }

        const rect = track.getBoundingClientRect()
        const thumbWidth = thumb.offsetWidth
        const availableThumbTravel = track.clientWidth - thumbWidth

        if (availableThumbTravel <= 0) {
          return
        }

        const clickX = event.clientX - rect.left - (thumbWidth / 2)

        const ratio = Math.max(
          0,
          Math.min(1, clickX / availableThumbTravel)
        )

        const maxScrollLeft =
          scrollElement.scrollWidth - scrollElement.clientWidth

        scrollElement.scrollLeft = ratio * maxScrollLeft
      })

      requestAnimationFrame(() => {
        this.updateCustomScrollbar(scrollElement, scrollbar)
      })
    },

    updateCustomScrollbar (scrollElement, scrollbar) {
      if (!scrollElement || !scrollbar) {
        return
      }

      const track = scrollbar.querySelector('.custom-scrollbar-track')
      const thumb = scrollbar.querySelector('.custom-scrollbar-thumb')

      if (!track || !thumb) {
        return
      }

      const scrollWidth = scrollElement.scrollWidth
      const clientWidth = scrollElement.clientWidth
      const maxScrollLeft = scrollWidth - clientWidth

      if (maxScrollLeft <= 2) {
        scrollbar.classList.remove('is-visible')
        return
      }

      scrollbar.classList.add('is-visible')

      const trackWidth = track.clientWidth

      if (!trackWidth) {
        return
      }

      const thumbWidth = Math.max(
        30,
        (clientWidth / scrollWidth) * trackWidth
      )

      const availableThumbTravel = trackWidth - thumbWidth

      const ratio = maxScrollLeft > 0
        ? scrollElement.scrollLeft / maxScrollLeft
        : 0

      thumb.style.width = `${thumbWidth}px`
      thumb.style.transform =
        `translateX(${ratio * availableThumbTravel}px)`
    },

    enhanceTables () {
      const tables = this.$el.querySelectorAll('.markdown-body table')

      tables.forEach((table) => {
        if (
          table.parentElement &&
          table.parentElement.classList.contains('table-scroll')
        ) {
          const scrollElement = table.parentElement
          const wrapper = scrollElement.parentElement

          this.updateTableFade(wrapper, scrollElement)

          const scrollbar = wrapper.querySelector('.custom-scrollbar')
          this.updateCustomScrollbar(scrollElement, scrollbar)

          return
        }

        const wrapper = document.createElement('div')
        wrapper.className = 'table-scroll-wrap'

        const scrollElement = document.createElement('div')
        scrollElement.className = 'table-scroll'

        table.parentNode.insertBefore(wrapper, table)

        wrapper.appendChild(scrollElement)
        scrollElement.appendChild(table)

        const btn = document.createElement('button')
        btn.type = 'button'
        btn.className = 'table-download-btn'
        btn.setAttribute('aria-label', 'Download as CSV')
        btn.innerHTML = '<i class="mdi mdi-download" aria-hidden="true"></i>'

        btn.onclick = () => {
          const csvContent = this.tableToCSV(table)
          const timestamp = new Date().toISOString().slice(0, 10)
          this.downloadCSV(csvContent, `table-${timestamp}.csv`)

          btn.classList.add('is-downloaded')
          btn.setAttribute('aria-label', 'Downloaded')
          btn.innerHTML = '<i class="mdi mdi-check" aria-hidden="true"></i>'

          setTimeout(() => {
            btn.classList.remove('is-downloaded')
            btn.setAttribute('aria-label', 'Download as CSV')
            btn.innerHTML = '<i class="mdi mdi-download" aria-hidden="true"></i>'
          }, 1200)
        }

        wrapper.appendChild(btn)

        const handleScroll = () => {
          this.updateTableFade(wrapper, scrollElement)
        }

        scrollElement.addEventListener(
          'scroll',
          handleScroll,
          { passive: true }
        )

        scrollElement._assistantTableScrollHandler = handleScroll

        this.createCustomScrollbar(scrollElement, wrapper)

        requestAnimationFrame(() => {
          this.updateTableFade(wrapper, scrollElement)
        })
      })
    },

    updateTableFade (wrapper, scrollElement) {
      if (!wrapper || !scrollElement) {
        return
      }

      const maxScrollLeft =
        scrollElement.scrollWidth - scrollElement.clientWidth

      const threshold = 2

      const overflowing = maxScrollLeft > threshold

      const canScrollLeft =
        overflowing &&
        scrollElement.scrollLeft > threshold

      const canScrollRight =
        overflowing &&
        scrollElement.scrollLeft < maxScrollLeft - threshold

      wrapper.classList.toggle(
        'has-left-fade',
        canScrollLeft
      )

      wrapper.classList.toggle(
        'has-right-fade',
        canScrollRight
      )

      wrapper.classList.toggle(
        'is-scrollable',
        overflowing
      )

      const scrollbar = wrapper.querySelector('.custom-scrollbar')
      this.updateCustomScrollbar(scrollElement, scrollbar)
    },

    updateAllTableFades () {
      const wrappers =
        this.$el.querySelectorAll('.table-scroll-wrap')

      wrappers.forEach((wrapper) => {
        const scrollElement =
          wrapper.querySelector('.table-scroll')

        this.updateTableFade(wrapper, scrollElement)
      })
    },

    updateAllCodeScrollbars () {
      const wraps =
        this.$el.querySelectorAll('.code-wrap')

      wraps.forEach((wrap) => {
        const pre = wrap.querySelector('pre')
        const scrollbar = wrap.querySelector('.custom-scrollbar')

        this.updateCustomScrollbar(pre, scrollbar)
      })
    },

    handleWindowResize () {
      if (this.resizeFrame) {
        cancelAnimationFrame(this.resizeFrame)
      }

      this.resizeFrame = requestAnimationFrame(() => {
        this.resizeFrame = null
        this.updateAllTableFades()
        this.updateAllCodeScrollbars()
      })
    },

    enhanceCodeBlocks () {
      const blocks = this.$el.querySelectorAll('.markdown-body pre')

      blocks.forEach((pre) => {
        if (
          pre.parentElement &&
          pre.parentElement.classList.contains('code-wrap')
        ) {
          const wrap = pre.parentElement
          const scrollbar = wrap.querySelector('.custom-scrollbar')

          this.updateCustomScrollbar(pre, scrollbar)
          return
        }

        const wrap = document.createElement('div')
        wrap.className = 'code-wrap'
        pre.parentNode.insertBefore(wrap, pre)
        wrap.appendChild(pre)

        const btn = document.createElement('button')
        btn.type = 'button'
        btn.className = 'code-copy-btn'
        btn.setAttribute('aria-label', 'Copy code')
        btn.innerHTML = '<i class="mdi mdi-content-copy" aria-hidden="true"></i>'

        btn.onclick = async () => {
          await this.copyText(pre.innerText)
          btn.classList.add('is-copied')
          btn.setAttribute('aria-label', 'Copied code')
          btn.innerHTML = '<i class="mdi mdi-check" aria-hidden="true"></i>'

          setTimeout(() => {
            btn.classList.remove('is-copied')
            btn.setAttribute('aria-label', 'Copy code')
            btn.innerHTML = '<i class="mdi mdi-content-copy" aria-hidden="true"></i>'
          }, 1200)
        }

        wrap.appendChild(btn)

        this.createCustomScrollbar(pre, wrap)
      })
    },

    markCopied () {
      this.copied = true
      this.clearCopiedTimer()

      if (this.tooltipElement) {
        this.showTooltip('copy')
      }

      this.copiedTimer = setTimeout(() => {
        this.copied = false
        this.copiedTimer = null

        if (this.tooltipElement) {
          this.showTooltip('copy')
        }
      }, 1200)
    },

    clearCopiedTimer () {
      if (!this.copiedTimer) {
        return
      }

      clearTimeout(this.copiedTimer)
      this.copiedTimer = null
    }
  }
}
</script>

<style scoped>
.message-row {
  display: flex;
  margin: 8px 0 14px;
}

.message-col {
  max-width: 100%;
  min-width: 0;
}

.message-wrap {
  position: relative;
  padding-bottom: 20px;
  overflow: visible;
}

.message-text {
  width: 100%;
  min-width: 0;
  word-break: break-word;
  overflow-wrap: break-word;
  color: #041F30;
  font-family: 'Inter';
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  padding: 0;
  background: transparent;
}

.stopped-response {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  margin-bottom: 8px;
}

.stopped-line {
  flex: 1;
  height: 0;
  border-top: 1px solid #CFD9F4;
  min-width: 20px;
}

.stopped-text {
  padding: 0 12px;
  color: #041F30;
  font-family: 'Inter';
  font-size: 12px;
  font-weight: 300;
  line-height: 21px;
  white-space: nowrap;
}

.message-actions {
  position: absolute;
  left: 0;
  bottom: 0;
  display: flex;
  gap: 2px;
  overflow: visible;
}

.action-button .v-icon {
  color: #1C3880 !important;
}

.action-button:hover .v-icon {
  color: #1C3880 !important;
}

.action-button {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  padding: 0;
  border: none;
  background: transparent;
  color: #1C3880 !important;
  cursor: pointer;
  border-radius: 3px;
  transition: all 0.15s ease;
  overflow: visible;
}

.action-button:hover {
  color: #1C3880 !important;
  background: #ffffff !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
}

.action-button:active {
  transform: scale(0.95);
}

.action-button:disabled {
  opacity: 0.5;
  box-shadow: none;
}

.action-button:disabled:hover {
  background: transparent;
}

.markdown-body {
  width: 100%;
  min-width: 0;
  overflow: visible;
}

.markdown-body ::v-deep h1 {
  margin: 0 0 8px;
  color: #041F30;
  font-size: 22px;
  line-height: 1.35;
}
.markdown-body ::v-deep h2{
  margin: 0 0 8px;
  color: #041F30;
  font-size: 20px;
  line-height: 1.35;
}
.markdown-body ::v-deep h3{
  margin: 0 0 8px;
  color: #041F30;
  font-size: 18px;
  line-height: 1.35;
}
.markdown-body ::v-deep h4 {
  margin: 0 0 8px;
  color: #041F30;
  font-size: 16px;
  line-height: 1.35;
}

.markdown-body ::v-deep p,
.markdown-body ::v-deep ul,
.markdown-body ::v-deep ol,
.markdown-body ::v-deep pre,
.markdown-body ::v-deep blockquote {
  margin: 0 0 10px;
}

.markdown-body ::v-deep ul,
.markdown-body ::v-deep ol {
  padding-left: 20px;
}

.markdown-body ::v-deep li {
  margin: 0 0 2px;
}

.markdown-body ::v-deep dl {
  margin: 0 0 10px;
}

.markdown-body ::v-deep dt {
  margin-top: 6px;
  font-weight: 600;
  color: #041F30;
}

.markdown-body ::v-deep dd {
  margin: 0 0 8px 16px;
  color: #041F30;
}

.markdown-body ::v-deep .footnote-ref {
  color: #041F30;
  font-size: 0.8em;
  vertical-align: super;
}

.markdown-body ::v-deep .footnotes {
  margin-top: 12px;
  padding-top: 8px;
  border-top: 1px solid rgba(4, 31, 48, 0.12);
}

.markdown-body ::v-deep .footnotes ol {
  padding-left: 20px;
}

.markdown-body ::v-deep details {
  margin: 0 0 10px;
  padding: 8px 12px;
  border: 1px solid rgba(4, 31, 48, 0.12);
  border-radius: 6px;
}

.markdown-body ::v-deep summary {
  cursor: pointer;
  font-weight: 600;
  color: #0D2940;
}

.markdown-body ::v-deep .katex-display {
  margin: 10px 0;
  overflow-x: auto;
  overflow-y: hidden;
}

.markdown-body ::v-deep .katex {
  color: #16364A;
}

.markdown-body ::v-deep blockquote.md-alert {
  margin: 0 0 10px;
  padding: 10px 12px;
  border-left: 4px solid #9CB6F5;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.3);
}

.markdown-body ::v-deep .md-alert-title {
  margin-bottom: 4px;
  font-weight: 700;
}

.markdown-body ::v-deep blockquote.md-alert-note {
  border-left-color: #4A90E2;
}

.markdown-body ::v-deep blockquote.md-alert-tip {
  border-left-color: #2EA043;
}

.markdown-body ::v-deep blockquote.md-alert-warning {
  border-left-color: #D29922;
}

.markdown-body ::v-deep blockquote.md-alert-caution {
  border-left-color: #CF222E;
}

.markdown-body ::v-deep .custom-scrollbar {
  display: none;
  width: 100%;
  height: 10px;
  padding: 3px 0;
  box-sizing: border-box;
}

.markdown-body ::v-deep .custom-scrollbar.is-visible {
  display: block;
}

.markdown-body ::v-deep .custom-scrollbar-track {
  position: relative;
  width: 100%;
  height: 4px;
  border-radius: 999px;
  background: rgba(207, 217, 244, 0.45);
  cursor: default;
}

.markdown-body ::v-deep .custom-scrollbar-thumb {
  position: absolute;
  top: 0;
  left: 0;
  height: 4px;
  min-width: 30px;
  border-radius: 999px;
  background: #9CB6F5;
  cursor: default;
  will-change: transform;
}

.markdown-body ::v-deep .custom-scrollbar-thumb:hover {
  background: #86A5EE;
}

.markdown-body ::v-deep .custom-scrollbar-thumb:active {
  cursor: default;
}

.markdown-body ::v-deep .table-scroll-wrap {
  position: relative;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  margin: 18px 0 12px;
}

.markdown-body ::v-deep .table-scroll {
  position: relative;
  width: 100%;
  max-width: 100%;
  min-width: 0;

  overflow-x: auto;
  overflow-y: hidden;

  -webkit-overflow-scrolling: touch;

  scrollbar-width: none;
  -ms-overflow-style: none;
}

.markdown-body ::v-deep .table-scroll::-webkit-scrollbar {
  display: none;
  width: 0;
  height: 0;
}

.markdown-body ::v-deep .table-scroll-wrap.has-right-fade .table-scroll {
  -webkit-mask-image:
    linear-gradient(
      to right,
      #000 0,
      #000 calc(100% - 46px),
      transparent 100%
    );

  mask-image:
    linear-gradient(
      to right,
      #000 0,
      #000 calc(100% - 46px),
      transparent 100%
    );
}

.markdown-body ::v-deep .table-scroll-wrap.has-left-fade:not(.has-right-fade) .table-scroll {
  -webkit-mask-image:
    linear-gradient(
      to right,
      transparent 0,
      #000 46px,
      #000 100%
    );

  mask-image:
    linear-gradient(
      to right,
      transparent 0,
      #000 46px,
      #000 100%
    );
}

.markdown-body ::v-deep .table-scroll-wrap.has-left-fade.has-right-fade .table-scroll {
  -webkit-mask-image:
    linear-gradient(
      to right,
      transparent 0,
      #000 46px,
      #000 calc(100% - 46px),
      transparent 100%
    );

  mask-image:
    linear-gradient(
      to right,
      transparent 0,
      #000 46px,
      #000 calc(100% - 46px),
      transparent 100%
    );
}

.markdown-body ::v-deep table {
  width: max-content;
  min-width: 100%;
  margin: 0;

  border: 0;
  border-collapse: collapse;
  border-spacing: 0;

  background: transparent;
  box-shadow: none;
  border-radius: 0;

  color: #041F30;
  font-family: 'Inter';
  font-size: 14px;
  line-height: 20px;
}

.markdown-body ::v-deep th,
.markdown-body ::v-deep td {
  height: 45px;
  padding: 10px 20px;

  border: 0;
  border-bottom: 1px solid rgba(4, 31, 48, 0.09);

  text-align: left;
  vertical-align: middle;

  white-space: nowrap;

  background: transparent;
  color: #041F30;
}

.markdown-body ::v-deep th {
  height: 44px;

  color: #041F30;

  font-size: 14px;
  font-weight: 600;
  line-height: 18px;

  background: transparent;
}

.markdown-body ::v-deep tr:nth-child(even) td {
  background: transparent;
}

.markdown-body ::v-deep tbody tr:hover td {
  background: rgba(255, 255, 255, 0.14);
  transition: background-color 120ms ease;
}

.markdown-body ::v-deep tbody tr:last-child td {
  border-bottom: 0;
}

.markdown-body ::v-deep th:first-child,
.markdown-body ::v-deep td:first-child {
  padding-left: 20px;
}

.markdown-body ::v-deep th:last-child,
.markdown-body ::v-deep td:last-child {
  padding-right: 28px;
}

.markdown-body ::v-deep .code-wrap {
  position: relative;
  width: 100%;
  max-width: 100%;
  min-width: 0;

  margin: 16px 0;

  border-top: 1px solid rgba(4, 31, 48, 0.08);
  border-bottom: 1px solid rgba(4, 31, 48, 0.08);

  border-radius: 0;

  background: rgba(255, 255, 255, 0.18);

  overflow: hidden;
}

.markdown-body ::v-deep pre {
  width: 100%;
  max-width: 100%;
  min-width: 0;

  margin: 0 !important;
  padding: 16px 18px !important;

  border: 0 !important;
  border-radius: 0 !important;

  background: transparent !important;

  box-shadow: none !important;
  outline: none !important;

  overflow-x: auto;
  overflow-y: hidden;

  -webkit-overflow-scrolling: touch;

  scrollbar-width: none;
  -ms-overflow-style: none;
}

.markdown-body ::v-deep pre::-webkit-scrollbar {
  display: none;
  width: 0;
  height: 0;
}

.markdown-body ::v-deep pre:focus,
.markdown-body ::v-deep pre code:focus {
  outline: none !important;
}

.markdown-body ::v-deep pre code {
  display: block;

  width: max-content;
  min-width: 100%;

  margin: 0 !important;
  padding: 0 !important;

  border: 0 !important;
  border-radius: 0 !important;

  background: transparent !important;

  box-shadow: none !important;

  font-family:
    ui-monospace,
    SFMono-Regular,
    Menlo,
    Monaco,
    Consolas,
    'Liberation Mono',
    monospace;

  font-size: 14px;
  font-weight: 400;
  line-height: 20px;

  color: #16364A;

  white-space: pre;
  tab-size: 2;
}

.markdown-body ::v-deep :not(pre) > code {
  display: inline;

  padding: 1px 4px;

  border: 0;
  border-radius: 3px;

  background: rgba(207, 217, 244, 0.32);

  color: #16364A;

  font-family:
    ui-monospace,
    SFMono-Regular,
    Menlo,
    Monaco,
    Consolas,
    monospace;

  font-size: 0.92em;
  font-weight: 400;
}

.markdown-body ::v-deep .code-copy-btn {
  position: absolute;
  top: 8px;
  right: 8px;

  z-index: 2;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  width: 26px;
  height: 26px;

  padding: 0;

  border: 0;
  border-radius: 4px;

  color: #1C3880;

  box-shadow: none;

  opacity: 0;

  cursor: pointer;

  transition:
    opacity 120ms ease,
    color 120ms ease,
    background-color 120ms ease,
    box-shadow 120ms ease;
}

.markdown-body ::v-deep .code-copy-btn i {
  font-size: 16px;
  line-height: 1;
}

.markdown-body ::v-deep .code-wrap:hover .code-copy-btn {
  opacity: 1;
}

.markdown-body ::v-deep .code-copy-btn:hover {
  color: #1C3880;

  background: #FFFFFF;

  box-shadow:
    0 2px 7px rgba(4, 31, 48, 0.08);
}

.markdown-body ::v-deep .code-copy-btn:active {
  transform: scale(0.96);
}

.markdown-body ::v-deep .code-copy-btn.is-copied {
  color: #1C3880;
}

.markdown-body ::v-deep .table-download-btn {
  position: absolute;
  top: 8px;
  right: 8px;

  z-index: 2;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  width: 26px;
  height: 26px;

  padding: 0;

  border: 0;
  border-radius: 4px;

  color: #1C3880;

  box-shadow: none;

  opacity: 0;

  cursor: pointer;

  transition:
    opacity 120ms ease,
    color 120ms ease,
    background-color 120ms ease,
    box-shadow 120ms ease;
}

.markdown-body ::v-deep .table-download-btn i {
  font-size: 16px;
  line-height: 1;
}

.markdown-body ::v-deep .table-scroll-wrap:hover .table-download-btn {
  opacity: 1;
}

.markdown-body ::v-deep .table-download-btn:hover {
  color: #1C3880;

  background: #FFFFFF;

  box-shadow:
    0 2px 7px rgba(4, 31, 48, 0.08);
}

.markdown-body ::v-deep .table-download-btn:active {
  transform: scale(0.96);
}

.markdown-body ::v-deep .table-download-btn.is-downloaded {
  color: #041F30;
}

.widget-row {
  margin: 10px 0 !important;
  width: 100%;
}
</style>

<style>
@keyframes tooltipFadeIn {
  from {
    opacity: 0;
    transform: translateX(-50%) translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
}

.custom-scrollbar-dragging {
  user-select: none;
}
</style>
